import React from "react";
import QuoteForm from "../Components/QuoteForm";
import SEO from "../Components/SEO";

const RequestQuote = () => {
  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');

    .rq-root { font-family: 'DM Sans', sans-serif; background: #fafaf8; }

    /* Hero */
    .rq-hero { background: #111; border-bottom: 1px solid rgba(255,255,255,0.06); padding: 10rem 7vw 7rem; position: relative; overflow: hidden; }
    .rq-hero-watermark { position: absolute; top: 50%; right: 5vw; transform: translateY(-50%); font-family: 'DM Serif Display', serif; font-size: clamp(6rem, 18vw, 16rem); color: rgba(255,255,255,0.025); line-height: 1; user-select: none; pointer-events: none; white-space: nowrap; }
    .rq-hero-glow { position: absolute; bottom: -30%; left: -10%; width: 60%; height: 80%; background: radial-gradient(circle, rgba(231,135,48,0.06) 0%, transparent 70%); pointer-events: none; }
    .rq-hero-content { position: relative; z-index: 1; max-width: 650px; }
    .rq-eyebrow { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 2rem; }
    .rq-eyebrow-line { width: 28px; height: 1px; background: #E78730; flex-shrink: 0; }
    .rq-eyebrow-text { font-size: 0.68rem; letter-spacing: 0.26em; text-transform: uppercase; color: #E78730; font-weight: 500; }
    .rq-hero h1 { font-family: 'DM Serif Display', serif; font-size: clamp(3rem, 7vw, 6rem); line-height: 0.95; color: #fff; margin-bottom: 2rem; }
    .rq-hero h1 em { color: #E78730; font-style: italic; }
    .rq-hero-desc { font-size: 0.95rem; color: rgba(255,255,255,0.38); line-height: 1.8; max-width: 48ch; font-weight: 300; }

    /* Form section */
    .rq-body { display: grid; grid-template-columns: 1fr 1.6fr; min-height: 80vh; }

    .rq-sidebar {
      padding: 5rem 5vw;
      border-right: 1px solid #e8e8e4;
      display: flex; flex-direction: column; justify-content: flex-start; gap: 0;
    }

    .rq-sidebar-label { font-size: 0.62rem; letter-spacing: 0.22em; text-transform: uppercase; color: #E78730; font-weight: 500; margin-bottom: 1.5rem; display: flex; align-items: center; gap: 0.5rem; }
    .rq-sidebar-label::before { content: ''; display: block; width: 14px; height: 1px; background: #E78730; flex-shrink: 0; }

    .rq-sidebar h2 { font-family: 'DM Serif Display', serif; font-size: clamp(1.75rem, 3vw, 2.5rem); color: #1a1a1a; line-height: 1.05; margin-bottom: 1.5rem; }
    .rq-sidebar h2 em { color: #E78730; font-style: italic; }
    .rq-sidebar-desc { font-size: 0.88rem; color: #888; line-height: 1.8; font-weight: 300; margin-bottom: 3rem; }

    .rq-sidebar-divider { width: 100%; height: 1px; background: #e8e8e4; margin-bottom: 2.5rem; }

    .rq-promise-item { display: flex; align-items: flex-start; gap: 1rem; margin-bottom: 1.5rem; }
    .rq-promise-item:last-child { margin-bottom: 0; }
    .rq-promise-tick { width: 18px; height: 18px; border: 1px solid #E78730; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
    .rq-promise-text { font-size: 0.85rem; color: #555; font-weight: 300; line-height: 1.6; }

    .rq-form-pane { padding: 5rem 5vw; }

    @media (max-width: 900px) {
      .rq-body { grid-template-columns: 1fr; }
      .rq-sidebar { border-right: none; border-bottom: 1px solid #e8e8e4; padding: 4rem 7vw; }
      .rq-form-pane { padding: 4rem 7vw; }
    }
  `;

  const promises = [
    "Detailed cost breakdown with transparent pricing",
    "Response within 24 business hours",
    "No hidden fees or unexpected costs",
    "Expert recommendations tailored to your needs",
    "Free consultation included with every quote",
  ];

  return (
    <>
      <SEO
        title="Request a Quote | Trademin Ltd"
        description="Request a free quote for electrical and mechanical services from Trademin Ltd. Fast, reliable service across Ashburton and Canterbury."
        canonical="https://www.trademin.co.nz/request-quote"
        ogTitle="Request a Quote | Trademin Ltd"
        ogDescription="Get a free quote for professional electrical and mechanical services in Canterbury."
      />
      <style>{styles}</style>

      <div className="rq-root">
        {/* Hero */}
        <section className="rq-hero">
          <div className="rq-hero-glow" />
          <div className="rq-hero-watermark">QUOTE</div>
          <div className="rq-hero-content">
            <div className="rq-eyebrow">
              <span className="rq-eyebrow-line" />
              <span className="rq-eyebrow-text">No Obligation</span>
            </div>
            <h1>
              Request a <em>Free Quote</em>
            </h1>
            <p className="rq-hero-desc">
              Fill out the form below and we'll get back to you within 24 hours
              with a detailed, no-obligation quote.
            </p>
          </div>
        </section>

        {/* Body */}
        <div className="rq-body">
          {/* Left sidebar */}
          <div className="rq-sidebar">
            <div className="rq-sidebar-label">What to Expect</div>
            <h2>
              We Make It <em>Simple</em>
            </h2>
            <p className="rq-sidebar-desc">
              Submit your project details and our team will prepare a
              comprehensive quote tailored to your exact requirements.
            </p>

            <div className="rq-sidebar-divider" />

            {promises.map((p, i) => (
              <div key={i} className="rq-promise-item">
                <div className="rq-promise-tick">
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#E78730"
                    strokeWidth="2.5"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <span className="rq-promise-text">{p}</span>
              </div>
            ))}
          </div>

          {/* Right form pane */}
          <div className="rq-form-pane">
            <QuoteForm />
          </div>
        </div>
      </div>
    </>
  );
};

export default RequestQuote;
